import { CheckCheck, ChevronDown, CircleCheckBig, FileText, MoreHorizontal, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/lib/auth/AuthProvider'
import { toast } from 'sonner'
import { confirmModal } from '@/components/ui/modal'
import { fmtMoney } from '@/features/shared/format'
import { useApprovePayment, useMarkPaymentsPaid, useRejectPayment, type OdemeView } from '../../api'
import { printDekont } from '../../lib/exportActions'
import { todayIso } from '../../lib/fileExport'
import { HR_OWNER_APPROVE_PERM, HR_PAYMENT_WRITE_PERM } from '../../../permissions'
import { apiErrorMessage } from '@/i18n'

export function OdemeRowActions({ odeme, variant = 'icon' }: { odeme: OdemeView; variant?: 'icon' | 'button' }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const approve = useApprovePayment()
  const reject = useRejectPayment()
  const markPaid = useMarkPaymentsPaid()
  // Onay / ret patron yetkisi; ödendi işareti ödeme yazma yetkisi ve patron onayı ister (BE kuralıyla aynı).
  const canOwner = can(HR_OWNER_APPROVE_PERM)
  const canPay = can(HR_PAYMENT_WRITE_PERM) && odeme.onaylandi && odeme.durum !== 'odendi'
  const ref = { id: odeme.id, employeeId: odeme.personel_id }
  const fail = (error: unknown) => toast.error(apiErrorMessage(error))
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        {variant === 'icon' ? (
          <Button
            variant="ghost"
            size="icon"
            className="size-8 text-muted-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
            aria-label={t('hr.personel.columns.islem')}
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="size-4" />
          </Button>
        ) : (
          <Button size="sm">
            {t('hr.personel.odemeler.islem')}
            <ChevronDown />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-xl" onClick={(e) => e.stopPropagation()}>
        {canOwner && odeme.durum === 'onay_bekliyor' ? (
          <DropdownMenuItem onSelect={() => approve.mutate(ref, { onSuccess: () => toast.success(t('hr.personel.actions.odeme.approved', { name: odeme.ad })), onError: fail })}>
            <CheckCheck />
            {t('hr.personel.actions.odeme.approve')}
          </DropdownMenuItem>
        ) : null}
        {canPay ? (
          <DropdownMenuItem
            onSelect={() =>
              markPaid.mutate(
                { rows: [odeme], paidAt: todayIso() },
                { onSuccess: () => toast.success(t('hr.personel.actions.odeme.paid', { name: odeme.ad })), onError: fail },
              )
            }
          >
            <CircleCheckBig />
            {t('hr.personel.actions.odeme.markPaid')}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onSelect={() => printDekont(odeme, odeme.iban || undefined)}>
          <FileText />
          {t('hr.personel.actions.odeme.dekont')}
        </DropdownMenuItem>
        {canOwner && odeme.durum === 'onay_bekliyor' ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                void confirmModal({
                  title: t('confirm.odemeDelete.title'),
                  description: t('confirm.odemeDelete.body', { name: odeme.ad, amount: fmtMoney(odeme.tutar) }),
                  confirmLabel: t('hr.personel.actions.odeme.remove'),
                  destructive: true,
                }).then((ok) => {
                  if (!ok) return
                  reject.mutate(
                    ref,
                    {
                      onSuccess: () => toast.success(t('hr.personel.actions.odeme.removed', { name: odeme.ad })),
                      onError: fail,
                    },
                  )
                })
              }}
              className="text-destructive data-[highlighted]:bg-destructive/10 data-[highlighted]:text-destructive [&_svg]:text-destructive"
            >
              <Trash2 />
              {t('hr.personel.actions.odeme.remove')}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
